import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { TabContent } from './TabPane';
import api from '../../services';
import { AttributePane } from './AttributePane';
import { DailyRestrictionPane } from './DailyRestrictionPane';
import AffiliateQualityScorePane from './AffiliateQualityScorePane';
import { PrePingPane } from './PrePingPane';
import { UsersPane } from './UsersPane';
import { SummaryPane } from './SummaryPane/SummaryPane';
import AnalysisPane from './AnalysisPane';
import { getOfferSelector } from '../../reducers/OfferTitleReducer';
import { useAttributeSelectors } from './hooks';

const TAB_NAMES = [
  'Summary',
  'Users',
  'Attribute',
  'Affiliate Quality Score',
  'Pre-ping',
  'Daily Restriction',
  'Analysis',
];

const Criteria = (props) => {
  const { qualityScoreCriteria } = useAttributeSelectors();
  const [state, setState] = useState({
    currentCapAmount: null,
    capRead: false,
  });
  const [activeTab, setActiveTab] = useState('Summary');
  const [collapse, setCollapse] = useState(true);

  const offerId = useSelector((state) => state.offerState.id);
  const offerActionCriteriaId = useSelector((state) => state.offerAction?.offerAction?.offerCriteria?.id);
  const offerWallAdCriteriaId = useSelector((state) => state.offerWallAd?.curOfferWallAd?.offerCriteria?.id);
  const sequenceCriteriaId = useSelector(
    (state) => state.sequence?.sequence?.offerCriteria?.id || state.sequence?.sequenceView?.sequence?.offerCriteria?.id
  );
  const landingPageOfferId = useSelector((state) => state.landingPage?.curLandingPage?.offer?.id);
  // Determine current id based on context
  const currentId =
    props.criteriaType === 'Offer Action Criteria'
      ? offerActionCriteriaId
      : props.criteriaType === 'Offer Wall Ad Criteria'
      ? offerWallAdCriteriaId
      : props.criteriaType === 'Sequence'
      ? sequenceCriteriaId
      : props.criteriaType === 'landingPage'
      ? landingPageOfferId
      : offerId;
  const limitAnswers = useSelector((state) => {
    const selector = getOfferSelector();
    let limitAnswers = selector ? selector(state, 'questionAnswers') : null;
    return limitAnswers
      ? limitAnswers
          .map((questionAnswer) => {
            if (questionAnswer && questionAnswer.text) {
              return questionAnswer.text;
            }
            return null;
          })
          .filter((answer) => answer !== null)
      : [];
  });

  const offerType = useSelector((state) => state.offerState && state.offerState.form);

  useEffect(() => {
    // Wait for the appropriate ID based on context
    if (props.criteriaType === 'Offer Action Criteria') {
      // For offer actions, wait until we have the criteriaId
      if (!offerActionCriteriaId) {
        return;
      }
    } else if (props.criteriaType === 'Offer Wall Ad Criteria') {
      // For offer wall ads, wait until we have the offerCriteriaId
      if (!offerWallAdCriteriaId) {
        return;
      }
    } else if (props.criteriaType === 'Sequence') {
      // For sequences, wait until we have the offerCriteriaId on sequence
      if (!sequenceCriteriaId) {
        return;
      }
    } else if (props.criteriaType === 'landingPage') {
      // For landing pages, wait until we have the offerId on the landing page
      if (!landingPageOfferId) {
        return;
      }
    } else {
      // For offers, wait until we have the offerId
      if (!offerId) {
        return;
      }
    }

    if (currentId && !state.capRead) {
      // Use Redis endpoint for both offer criteria and offer_criteria table
      const endpoint = `redis/affiliate-quality-score-current-caps/${currentId}?type=${
        props.criteriaType === 'Offer Action Criteria' ||
        props.criteriaType === 'Offer Wall Ad Criteria' ||
        props.criteriaType === 'Sequence'
          ? 'offer_criteria'
          : 'offer'
      }`;

      api
        .get(endpoint)
        .then((response) => {
          setState((prevState) => ({
            ...prevState,
            currentCapAmount: response.data,
            capRead: true,
          }));
        })
        .catch((error) => {
          console.error('Error fetching current caps:', error);
          setState((prevState) => ({
            ...prevState,
            capRead: true,
          }));
        });
    }
  }, [
    currentId,
    state.capRead,
    props.criteriaType,
    offerActionCriteriaId,
    offerWallAdCriteriaId,
    sequenceCriteriaId,
    landingPageOfferId,
    offerId,
  ]);

  return (
    <Card className='criteria-card'>
      <CardHeader
        onClick={() => setCollapse(!collapse)}
        sx={{ cursor: 'pointer' }}
        title={
          <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant='h4' component='h4'>
              Criteria
            </Typography>
            {collapse ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
          </Stack>
        }
        slotProps={{ title: { component: 'div' } }}
      />
      <CardContent>
        <Collapse in={collapse}>
          <Tabs
            value={activeTab}
            onChange={(_event, next) => setActiveTab(next)}
            variant='scrollable'
            scrollButtons='auto'
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {TAB_NAMES.map((tab) => (
              <Tab key={tab} value={tab} label={tab} />
            ))}
          </Tabs>
          <TabContent activeTab={activeTab} sx={{ p: 2 }}>
            <SummaryPane currentCaps={state.currentCapAmount} criteriaType={props.criteriaType} />
            <UsersPane criteriaType={props.criteriaType} />
            <AttributePane criteriaType={props.criteriaType} isActiveTab={activeTab === 'Attribute'} />
            <AffiliateQualityScorePane
              key={`quality-${
                state.currentCapAmount ? JSON.stringify(state.currentCapAmount).substring(0, 50) : 'empty'
              }`}
              offerId={currentId}
              type={props.criteriaType === 'Offer Action Criteria' ? 'offer_criteria' : 'offer'}
              currentCaps={state.currentCapAmount}
              qualityScoreCriteria={qualityScoreCriteria}
            />
            <PrePingPane criteriaType={props.criteriaType} />
            <DailyRestrictionPane
              currentCapAmount={state.currentCapAmount}
              capAmount={props.capAmount}
              capSegmentConfiguration={props.capSegmentConfiguration}
              criteriaType={props.criteriaType}
              criteriaId={offerActionCriteriaId}
              limitAnswers={limitAnswers}
              offerType={offerType}
            />
            <AnalysisPane source={props.source} />
          </TabContent>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default Criteria;
